import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Auth store — single source of truth for the JWT and the current user.
 *
 * Persists to localStorage under the key `merkflow-auth` so a hard refresh
 * keeps the session. The legacy plain-string `token` and `activeWorkspaceId`
 * keys (used by the Next.js codebase) are still read by lib/api.ts as a
 * fallback during migration; once Next is gone they get cleaned up.
 */

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  systemRole: 'user' | 'admin';
  onboardingCompleted: boolean;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setToken: (token: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      setToken: (token) => set({ token }),
      setUser: (user) => set({ user }),
      logout: () => {
        set({ token: null, user: null });
        // Clean up the legacy keys too. They were written by the Next.js
        // codebase via lib/api.ts setToken() and lib/auth.ts logout().
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('token');
          window.localStorage.removeItem('activeWorkspaceId');
        }
      },
    }),
    {
      name: 'merkflow-auth',
      storage: createJSONStorage(() => localStorage),
      // Don't persist `user` — it's fetched from /api/auth/me on every boot
      // anyway, and persisting it risks showing stale role/onboarding state.
      partialize: (state) => ({ token: state.token }),
    },
  ),
);
